import { useState, useEffect, useCallback } from 'react';

import {
  getPrompt,
  subscribe,
  clearPrompt,
  onInstalled,
  type BeforeInstallPromptEvent,
} from '@/lib/pwa/installPromptCapture';
import { onSWReady } from '@/lib/pwa/registerSW';

type InstallMode =
  | 'native'
  | 'manual-ios'
  | 'manual-android-firefox'
  | 'manual-android-generic';

interface UseInstallPromptReturn {
  isInstallable: boolean;
  isInstalled: boolean;
  installMode: InstallMode | null;
  promptInstall: () => Promise<boolean>;
  dismissPrompt: () => void;
}

const ANDROID_PROMPT_WAIT_MS = 3_000;

const isBrowser = typeof window !== 'undefined';

function detectIsIOS(): boolean {
  if (!isBrowser) return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function detectIsAndroid(): boolean {
  if (!isBrowser) return false;
  const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  if (uaData?.platform) {
    return uaData.platform.toLowerCase() === 'android';
  }
  return /Android/i.test(navigator.userAgent);
}

function detectIsChromiumBased(): boolean {
  if (!isBrowser) return false;
  // Todos os browsers Chromium (Chrome, Edge, Opera, Samsung) incluem "Chrome/" no UA
  return /Chrome\/\d+/.test(navigator.userAgent);
}

function detectIsFirefoxAndroid(): boolean {
  if (!isBrowser) return false;
  return /Android/i.test(navigator.userAgent) && /Firefox\/\d+/.test(navigator.userAgent);
}

function isStandaloneMode(): boolean {
  if (!isBrowser) return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator &&
      (navigator as { standalone?: boolean }).standalone === true)
  );
}

export function useInstallPrompt(): UseInstallPromptReturn {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(() => getPrompt());
  const [isInstalled, setIsInstalled] = useState(isStandaloneMode);
  // Dispensa apenas de sessão (em memória, sem storage): o usuário fecha a barra
  // e ela some enquanto ele navega (a barra vive no SidebarLayout, que não
  // desmonta entre rotas), mas um F5/reabrir aba reoferece a instalação —
  // padrão de e-commerce (Amazon, Mercado Livre). Persistir em localStorage
  // silenciaria o convite para sempre; sessionStorage sobreviveria ao F5.
  const [isDismissed, setIsDismissed] = useState(false);
  const [swReady, setSwReady] = useState(false);
  const [promptWaitDone, setPromptWaitDone] = useState(false);
  const [promptConsumed, setPromptConsumed] = useState(false);

  const isIOS = detectIsIOS();
  const isAndroid = detectIsAndroid();
  const isChromiumBased = detectIsChromiumBased();
  const isFirefoxAndroid = detectIsFirefoxAndroid();

  // Subscribe a mudanças no prompt (singleton)
  useEffect(() => {
    return subscribe((prompt) => setDeferredPrompt(prompt));
  }, []);

  // Sinal explícito de instalação (desacoplado do prompt)
  useEffect(() => {
    return onInstalled(() => setIsInstalled(true));
  }, []);

  // Gate: SW ready
  useEffect(() => {
    return onSWReady(() => setSwReady(true));
  }, []);

  // Gate: janela de observação para beforeinstallprompt (só Chromium dispara esse evento)
  useEffect(() => {
    if (!(isAndroid && isChromiumBased)) return;
    const timer = setTimeout(() => setPromptWaitDone(true), ANDROID_PROMPT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isAndroid, isChromiumBased]);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;

    setPromptConsumed(true);
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    clearPrompt();
    setDeferredPrompt(null);

    return outcome === 'accepted';
  }, [deferredPrompt]);

  const dismissPrompt = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // --- Máquina de estados ---
  const installMode: InstallMode | null = (() => {
    if (isInstalled || isDismissed || promptConsumed) return null;
    // Prompt nativo sempre tem prioridade (Chromium only)
    if (deferredPrompt) return 'native';
    // iOS: instruções de Share sheet
    if (isIOS) return 'manual-ios';
    // Android: instruções manuais por capacidade
    if (isAndroid && isBrowser && window.isSecureContext) {
      if (isChromiumBased) {
        // Chromium: esperar janela do beforeinstallprompt + SW ready
        if (promptWaitDone && swReady) return 'manual-android-generic';
      } else if (isFirefoxAndroid) {
        // Firefox Android: beforeinstallprompt nunca dispara,
        // SW não é requisito para instalabilidade (MDN).
        // Instrução manual imediata.
        return 'manual-android-firefox';
      }
      // Browsers Android não validados: sem banner (conservador)
    }
    return null;
  })();

  return {
    isInstallable: installMode !== null,
    isInstalled,
    installMode,
    promptInstall,
    dismissPrompt,
  };
}
